import { CustomThemeProvider, IconTooltip } from 'sonata-react-components';

import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';
import type { DataProcessResult } from '@/services/file.service';

const MAX_LISTED_EMPTY_ROWS = 10;

function getRowErrorString(preprocess: DataProcessResult | undefined, formatMessage: IntlShape['formatMessage']) {
  const uniqueRows = new Set(preprocess?.errors?.map(({ row }) => row) || []);
  const rows = [...uniqueRows];
  const andText = formatMessage({
    defaultMessage: 'and',
    id: 'common.and'
  });
  return rows.length > 1 ? `${rows.slice(0, -1).join(', ')} ${andText} ${rows[rows.length - 1]}` : rows[0];
}

export function getImportErrorsToastText(
  preprocess: DataProcessResult | undefined,
  formatMessage: IntlShape['formatMessage']
): { text: ReactNode; secondaryText: ReactNode } {
  const errorNumber = new Set(preprocess?.errors?.map(({ row }) => row)).size ?? 0;

  if (preprocess?.errors?.[0] && preprocess?.headers.length === 1) {
    return {
      secondaryText: formatMessage({
        defaultMessage: "Please check if the delimiter you've selected is correct.",
        id: 'poiImporter.status.error.incorrectDelimiter.detail'
      }),
      text: formatMessage({
        defaultMessage: 'It looks like the file configuration is incorrect.',
        id: 'poiImporter.status.error.incorrectDelimiter.headline'
      })
    };
  }

  if (preprocess?.errors?.length && preprocess.errors.every(({ type }) => type === 'Empty row')) {
    if (errorNumber > MAX_LISTED_EMPTY_ROWS) {
      const emptyRows = [...new Set(preprocess.errors.map(({ row }) => row))].sort((a, b) => a - b);
      return {
        secondaryText: formatMessage({
          defaultMessage:
            'Fix them in your file, or select "Remove the empty rows from my file" to continue without them.',
          id: 'poiImporter.status.error.manyEmptyRows.detail'
        }),
        text: (
          <span className="csvImporter__emptyRowsTotal">
            {formatMessage(
              {
                defaultMessage: 'Your file has a total of {total} empty rows.',
                id: 'poiImporter.status.error.manyEmptyRows.headline'
              },
              { total: errorNumber }
            )}
            <CustomThemeProvider themeContrast="light">
              <IconTooltip effect="solid" event="mouseenter" icon="fa-regular fa-circle-info" place="top">
                {formatMessage(
                  {
                    defaultMessage: '<label>Empty rows:</label> {rows}',
                    id: 'poiImporter.status.error.manyEmptyRows.tooltip'
                  },
                  {
                    label: (chunks: ReactNode[]) => <span className="csvImporter__emptyRowsTotalLabel">{chunks}</span>,
                    rows: emptyRows.join(', ')
                  }
                )}
              </IconTooltip>
            </CustomThemeProvider>
          </span>
        )
      };
    }

    return {
      secondaryText: formatMessage(
        {
          defaultMessage:
            '{total, plural, one {Row {row} is empty.} other {Rows {row} are empty.}} Fix your file, or select "Remove the empty rows from my file" to continue without them.',
          id: 'poiImporter.status.error.emptyRows.detail'
        },
        {
          row: getRowErrorString(preprocess, formatMessage),
          total: errorNumber
        }
      ),
      text: formatMessage({
        defaultMessage: 'Your file has empty rows.',
        id: 'poiImporter.status.error.emptyRows.headline'
      })
    };
  }

  if (
    (preprocess?.headers?.length ?? 0) > 1 &&
    errorNumber > 10 &&
    errorNumber < (preprocess?.dataPreview?.length ?? 0)
  ) {
    return {
      secondaryText: formatMessage({
        defaultMessage: 'Please check the preview to review which rows have errors.',
        id: 'poiImporter.status.error.manyErrors.detail'
      }),
      text: formatMessage({
        defaultMessage: 'The file data configuration is incorrect.',
        id: 'poiImporter.status.error.manyErrors.headline'
      })
    };
  }

  if (
    ((preprocess?.headers?.length ?? 0) > 1 &&
      preprocess?.errors.length === 2 &&
      preprocess?.errors?.[0]?.row === preprocess?.errors?.[1]?.row) ||
    preprocess?.errors.length === 1
  ) {
    return {
      secondaryText: formatMessage(
        {
          defaultMessage: 'Please review row {row} and column(s): {column}.',
          id: 'poiImporter.status.error.fullMessage.detail'
        },
        {
          column: preprocess?.errors
            ?.map(({ column }) => column)
            .join(
              ` ${formatMessage({
                defaultMessage: 'and',
                id: 'common.and'
              })} `
            ),
          row: preprocess?.errors?.[0]?.row
        }
      ),
      text: formatMessage({
        defaultMessage: 'The file data configuration is incorrect.',
        id: 'poiImporter.status.error.fullMessage.headline'
      })
    };
  }

  if ((preprocess?.headers?.length ?? 0) > 1 && errorNumber > 2 && errorNumber <= 10) {
    return {
      secondaryText: formatMessage(
        {
          defaultMessage: 'Please review rows: {row}.',
          id: 'poiImporter.status.error.severalRows.detail'
        },
        {
          row: getRowErrorString(preprocess, formatMessage)
        }
      ),
      text: formatMessage({
        defaultMessage: 'The file data configuration is incorrect.',
        id: 'poiImporter.status.error.severalRows.headline'
      })
    };
  }

  if (errorNumber >= (preprocess?.dataPreview?.length ?? 0)) {
    return {
      secondaryText: formatMessage({
        defaultMessage: "Please check if your column mapping (e.g., Latitude/Longitude) matches your file's structure.",
        id: 'poiImporter.status.error.incorrectColumnMapping.detail'
      }),
      text: formatMessage({
        defaultMessage: 'It looks like the file configuration is incorrect.',
        id: 'poiImporter.status.error.incorrectColumnMapping.headline'
      })
    };
  }

  return { secondaryText: '', text: '' };
}
